import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WidgetsIcon from '@mui/icons-material/Widgets';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAppDispatch } from '@/app/hooks';
import { toggleSidebar } from '@/app/uiSlice';
import { appConfig } from '@/config/app.config';
import { buildNavigation, routes } from '@/routes/routeConfig';
import { sidebar } from '@/theme/theme';

const ICONS: Record<string, ReactElement> = {
  dashboard: <DashboardIcon fontSize="small" />,
  receipt_long: <ReceiptLongIcon fontSize="small" />,
  groups: <GroupsIcon fontSize="small" />,
};

interface SidebarProps {
  collapsed: boolean;
  mobileSidebarOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ collapsed, mobileSidebarOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const appDispatch = useAppDispatch();

  const groups = useMemo(() => buildNavigation(routes), []);

  const [closedGroups, setClosedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (name: string) => {
    setClosedGroups((curr) => {
      const next = new Set(curr);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const width = collapsed ? appConfig.ui.sidebarCollapsedWidth : appConfig.ui.sidebarWidth;

  const paperSx = {
    width,
    bgcolor: sidebar.bg,
    color: sidebar.ink,
    border: 'none',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    transition: 'width 0.18s ease',
    display: 'flex',
    flexDirection: 'column',
  } as const;

  const content = (
    <>
      {/* Spacer matching the Header height, with the sidebar collapse toggle pinned top-right. */}
      <Box
        sx={{
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pr: '10px',
        }}
      >
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={() => appDispatch(toggleSidebar())}
            aria-label="Toggle navigation"
            size="small"
            sx={{
              color: sidebar.ink2,
              '&:hover': { color: sidebar.ink, bgcolor: sidebar.hover },
              width: 32,
              height: 32,
            }}
          >
            {collapsed ? (
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          px: '12px',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: sidebar.line, borderRadius: 4 },
        }}
      >
        {groups.map((group) => {
          const isClosed = closedGroups.has(group.name);

          return (
            <Box key={group.name} sx={{ mt: '14px' }}>
              {!collapsed && (
                <Box
                  onClick={() => toggleGroup(group.name)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '10px',
                    pb: '6px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10.5px',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: sidebar.ink2,
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      color: sidebar.ink2,
                      fontSize: 20,
                      fontWeight: 700,
                      transform: isClosed ? 'rotate(-90deg)' : 'none',
                      transition: 'transform .18s',
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                    }}
                  >
                    ▾
                  </Box>
                </Box>
              )}

              <Collapse in={!isClosed || collapsed} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {group.routes.map((route) => {
                    const to = `/${route.path}`;
                    const isActive =
                      location.pathname === to || location.pathname.startsWith(`${to}/`);
                    const icon = (route.icon && ICONS[route.icon]) ?? (
                      <WidgetsIcon fontSize="small" />
                    );

                    return (
                      <Tooltip
                        key={route.path}
                        title={collapsed ? (route.label ?? '') : ''}
                        placement="right"
                        arrow
                      >
                        <ListItemButton
                          component={NavLink}
                          to={to}
                          onClick={onMobileClose}
                          sx={{
                            minHeight: 40,
                            borderRadius: '9px',
                            mb: '2px',
                            px: collapsed ? 0 : '10px',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            gap: '11px',
                            position: 'relative',
                            color: sidebar.ink,
                            fontSize: '13.5px',
                            fontWeight: 500,
                            background: isActive ? sidebar.activeGrad : 'transparent',
                            '&:hover': { bgcolor: sidebar.hover },
                            '&.Mui-selected': { bgcolor: 'transparent' },
                            // Active accent bar
                            '&::before': isActive
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: -12,
                                  top: 8,
                                  bottom: 8,
                                  width: 3,
                                  borderRadius: '0 3px 3px 0',
                                  bgcolor: '#0FB5A6',
                                }
                              : {},
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: isActive ? '#fff' : sidebar.ink,
                              opacity: isActive ? 1 : 0.85,
                              flexShrink: 0,
                              '& svg': { width: 18, height: 18 },
                            }}
                          >
                            {icon}
                          </Box>
                          {!collapsed && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '13.5px',
                                fontWeight: 500,
                                color: isActive ? '#fff' : sidebar.ink,
                                flex: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {route.label}
                            </Typography>
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box
          sx={{
            borderTop: `1px solid ${sidebar.line}`,
            p: '12px 18px',
            fontSize: '11px',
            color: sidebar.ink2,
            lineHeight: 1.6,
          }}
        >
          <Typography
            sx={{ fontSize: 11, color: sidebar.ink2, fontFamily: (t) => t.otrack.monoFamily }}
          >
            v{appConfig.build.version}
          </Typography>
          <Typography sx={{ fontSize: 11, color: sidebar.ink2 }}>© 2026 OTrack</Typography>
        </Box>
      )}
    </>
  );

  return (
    <>
      {/* Permanent sidebar (desktop) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': paperSx,
        }}
      >
        {content}
      </Drawer>

      {/* Temporary sidebar (mobile) */}
      <Drawer
        variant="temporary"
        open={mobileSidebarOpen}
        onClose={onMobileClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { ...paperSx, width: appConfig.ui.sidebarWidth, boxShadow: 8 },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {content}
      </Drawer>
    </>
  );
}
